import { CheckboxProps } from "@mui/material";
import React, { useEffect } from "react";
import { TableBodyRow, Cell } from "./body";
import { HeaderCell, TableHeaderRow } from "./header";
import { typedMemo } from "@common/utils/types";
import { useAutoScroll } from "../useAutoScroll";
import { useDragDropManager } from "react-dnd";
import { ListDnDTypes } from "../const";

type IHeaderRowProps<T, K extends keyof T, O = undefined> = Pick<
  ListViewProps<T, K, O>,
  | "columns"
  | "onColumnsOrderChange"
  | "onColumnWidthChange"
  | "rowHeight"
  | "defaultWidth"
  | "pinFirstColumn"
  | "defaultMinWidth"
  | "selectable"
  | "disabledCheckbox"
  | "selectionBehavior"
  | "sort"
  | "onSort"
  | "disableColumnResize"
> & { CheckboxProps?: CheckboxProps };
const HeaderRowImpl = <T, K extends keyof T, O = undefined>(
  props: IHeaderRowProps<T, K, O> & {
    containerRef: React.RefObject<HTMLElement | null>;
  }
) => {
  const { columns, rowHeight, disableColumnResize } = props;

  const updateItemPosition = useAutoScroll(
    "horizontal",
    props.containerRef,
    // To uncomment when columns will be resizable and will all have a size as number
    props.pinFirstColumn ? columns[0].width ?? props.defaultWidth : undefined
  );
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();
  useEffect(() => {
    const unsubscribe = monitor.subscribeToOffsetChange(() => {
      const currentType = monitor.getItemType();
      if (currentType === ListDnDTypes.LIST_ORDER) {
        const offset = monitor.getClientOffset();
        updateItemPosition(offset);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [monitor, updateItemPosition]);
  return (
    <TableHeaderRow sx={{ height: rowHeight }}>
      {columns
        .filter((c) => c.show ?? true)
        .map((col, i) => {
          const isLastColumn = i === columns.length - 1;

          return (
            <HeaderCell
              key={String(col.id ?? col.key)}
              col={col}
              {...props}
              index={i}
              disableColumnResize={disableColumnResize || isLastColumn}
            />
          );
        })}
    </TableHeaderRow>
  );
};

interface IBodyRowProps<T, K extends keyof T, O = undefined>
  extends Pick<
    ListViewProps<T, K, O>,
    | "columns"
    | "rowHeight"
    | "pinFirstColumn"
    | "selectable"
    | "identityKey"
    | "isRowSelectable"
    | "isRowHighlighted"
    | "disabledCheckbox"
    | "selectionBehavior"
  > {
  row: T;
  onSelect?: () => void;
  checked?: boolean;
  customSelector?: React.FC<SelectorBehaviorProps>;
  listControls: ListControls<T>;
  notSelectableReason?: string;
}
const BodyRowImpl = <T, K extends keyof T, O = undefined>(
  props: IBodyRowProps<T, K, O>
) => {
  const {
    columns,
    row,
    rowHeight,
    pinFirstColumn,
    selectable = false,
    onSelect = () => {},
    isRowSelectable = () => true,
    isRowHighlighted = () => false,
    checked = false,
    disabledCheckbox,
    selectionBehavior,
    customSelector,
    listControls,
    notSelectableReason,
  } = props;

  const isHighlighted = isRowHighlighted(row);
  const isSelectable = isRowSelectable(row);

  return (
    <TableBodyRow sx={{ height: rowHeight }}>
      {columns
        .filter((c) => c.show ?? true)
        .map((col, i) => (
          <Cell
            key={String(col.id ?? col.key)}
            length={columns.length}
            index={i}
            pinFirstColumn={pinFirstColumn}
            selectable={selectable}
            onSelect={onSelect}
            customSelector={customSelector}
            isRowSelectable={isSelectable}
            checked={checked}
            highlighted={isHighlighted}
            disabledCheckbox={disabledCheckbox}
            selectionBehavior={selectionBehavior}
            notSelectableReason={notSelectableReason}
          >
            {typeof col.render === "undefined" ? (
              <>{`${row[col.key]}`}</>
            ) : (
              col.render(row, col, listControls)
            )}
          </Cell>
        ))}
    </TableBodyRow>
  );
};

// Coldefs are equals if they have all columns with the same order and values.
// FIXME: get rid of any
const areColDefsEquals = (a: ColDef<any>[], b: ColDef<any>[]) => {
  if (Object.is(a, b)) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = Object.entries(a[i]);
    const y = Object.entries(b[i]);
    if (x.length !== y.length) return false;
    if (
      x.some(
        ([key, value]) =>
          !y.find(([keyB, valueB]) => key === keyB && Object.is(value, valueB))
      )
    ) {
      return false;
    }
  }
  return true;
};

// FIXME: get rid of any
const areHeaderPropsEquals = (
  oldProps: IHeaderRowProps<any, keyof any>,
  newProps: IHeaderRowProps<any, keyof any>
): boolean => {
  const simpleProps: (keyof typeof oldProps)[] = [
    "defaultMinWidth",
    "defaultWidth",
    "disabledCheckbox",
    "pinFirstColumn",
    "selectable",
    "rowHeight",
    "selectionBehavior",
    "sort",
    "disableColumnResize",
  ];
  if (simpleProps.some((key) => !Object.is(oldProps[key], newProps[key])))
    return false;
  if (
    (oldProps.CheckboxProps === undefined) !==
    (newProps.CheckboxProps === undefined)
  )
    return false;
  else {
    const a = Object.entries(oldProps.CheckboxProps ?? {});
    const b = Object.entries(newProps.CheckboxProps ?? {});
    if (a.length !== b.length) return false;
    if (
      a.some(
        ([key, value]) =>
          !b.find(([keyB, valueB]) => key === keyB && Object.is(value, valueB))
      )
    )
      return false;
  }
  if (!areColDefsEquals(oldProps.columns, newProps.columns)) return false;
  return true;
};

// FIXME: get rid of any
const areBodyPropsEquals = (
  oldProps: IBodyRowProps<any, keyof {}>,
  newProps: IBodyRowProps<any, keyof {}>
): boolean => {
  const simpleProps: (keyof typeof oldProps)[] = [
    "columns",
    "disabledCheckbox",
    "selectionBehavior",
    "rowHeight",
    "pinFirstColumn",
    "selectable",
    "identityKey",
    "checked",
  ];
  if (simpleProps.some((key) => !Object.is(oldProps[key], newProps[key])))
    return false;
  if (JSON.stringify(oldProps.row) !== JSON.stringify(newProps.row))
    return false;
  if (!areColDefsEquals(oldProps.columns, newProps.columns)) return false;
  return true;
};
export const HeaderRow = typedMemo(HeaderRowImpl, areHeaderPropsEquals);
export const BodyRow = typedMemo(BodyRowImpl, areBodyPropsEquals);
